import React, { useEffect, useRef, useState } from 'react';
import { categoryList, subCategoryList, menuItemList, menuItemDetail, spiceLevelsOptions } from '../services/menu.service';
import Skeleton from 'react-loading-skeleton';
import { CONSUMPTION_TAX_RATES, DISPLAY_METHOD_TAX_OPTIONS, ITEM_TYPES, LOADER_TIMEOUT_TIME, ORDER_PLATFORM, QR_MENU_ITEMS_INITIAL_PAGE, QR_MENU_ITEMS_PER_PAGE, QR_MENU_SCROLL_LOAD_OFFSET, SOCKET_CLIENT_EVENTS, SOCKET_EVENTS, SPICE_LEVELS, SWAL_SETTINGS } from '../../../utils/Constants';
import { SOCKET_CLIENT } from '../../../utils/socket';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from "formik";
import { getAddonKey, getPersistentSessionKey, getSubTotalQRApp, getTranslatedKey, getUniqueID, isSameArray, pluralText, showPrice, TrimText } from '../../../utils/commonfunction';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import Swal from "sweetalert2";
import RecordNotFound from '../../restaurant/shared/recordNotFound';
import { SetCartLength } from '../../../redux/slices/qrcodeDetails';
import InlineLoader from '../shared/inlineLoader';
import { stopQrNativeLoader } from '../utils/nativeBridge';
import { getDisplayableDescription, hasDisplayablePrice, isOrderableMenuItem } from '../utils/menuItemVisibility';
import { readVisitCart, saveVisitCart } from '../utils/qrVisit';
import SetMenuAddonPicker from '../components/SetMenuAddonPicker';
import { calculateSetMenuPrice } from '../../cashier/utils/setMenuAddons';
import { expandByQuantity } from '../../../utils/addonQuantity';

const ReducedTaxFootnote = ({ items, chargeSettings, t }) => {
  const hasReducedTax =
    getSubTotalQRApp(items, chargeSettings)?.total_tax_8 > 0 &&
    chargeSettings?.icon_display_eligible_for_reduced_tax_rate;

  if (!hasReducedTax) {
    return null;
  }

  return (
    <div className="text-end small mt-3">
      * {t('tax_on_other_text')}
      <br />
      {t('tax_rate_8')}
    </div>
  );
};

export default function AddMenu() {
  const queryParams = new URLSearchParams(window.location.search);
  const restaurantId = queryParams.get('restaurant_id') || '';
  const tableId = queryParams.get('table_id') || '';
  const getStoredCartItems = () => readVisitCart(restaurantId, tableId);
  const [token, setToken] = useState(getPersistentSessionKey("qr_user_token"));
  const [categoriesList, setCategoriesList] = useState([]);
  const [itemList, setItemList] = useState([]);
  const [itemPage, setItemPage] = useState(QR_MENU_ITEMS_INITIAL_PAGE);
  const [hasMoreItems, setHasMoreItems] = useState(false);
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState(false);
  const [catLoader, setCatLoader] = useState(true);
  const [itemLoader, setItemLoader] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(queryParams.get('category_id') || '');
  const [subCategoriesList, setSubCategoriesList] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(queryParams.get('sub_category_id') || '');
  const [subCatLoader, setSubCatLoader] = useState(false);
  const [currentItem, setCurrentItem] = useState({});
  // The item's own configured choice groups, as the menu defines them. An
  // immutable catalogue: the rows on the line are built from it and counted
  // against it, but it never changes while the popup is open. The order path
  // accepts no other group, so nothing else is ever offered.
  const [itemGroups, setItemGroups] = useState([]);
  const addFormCloseButton = useRef();
  const addFormOpenButton = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [langChanged, setLangChanged] = useState(false);
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState(getStoredCartItems());
  const [customerDetails, setCustomerDetails] = useState(location?.state?.customerDetails || null);
  const CartLengthData = useSelector((state) => state.QrOrderToken.CartLength);
  const dispatch = useDispatch();
  const base = queryParams.get('base');
  const ChargeSettings = useSelector((state) => {
    return state.QrOrderToken.ChargeSettings;
  });
  const [removableItems, setRemovableItems] = useState([]);
  const removableItemsOpenButton = useRef();
  const removableItemsCloseButton = useRef();
  const newToken = useSelector((state) => { return state.QrOrderToken.qrPanelToken });
  const [disableBtn, setDisableBtn] = useState(true);

  const [optionsData, setOptionsData] = useState([]);

  useEffect(() => {
    if (token && token.length) {
      // setCatLoader(true);
      spiceLevelsOptions({}, { token: token })
        .then((response) => {
          setOptionsData(response.data.list);
        })
        .catch((err) => { console.error(err) });
    }
  }, [token, langChanged]);

  // WRDS-860: live sold-out grey-out. Subscribe to the PUBLIC per-restaurant
  // availability room (carries only availability events, safe for anonymous QR
  // customers — no base-gate needed). Patch the affected item's flag in place.
  useEffect(() => {
    if (!restaurantId) return;
    const handleSocketData = (response) => {
      if (response?.event === SOCKET_EVENTS.MENU_ITEM_AVAILABILITY_CHANGED) {
        setItemList((prev) =>
          prev.map((it) =>
            it?._id === response?.data?.item_id
              ? { ...it, is_available: response.data.is_available }
              : it
          )
        );
      }
    };
    // socket.io drops room membership on disconnect, so re-join on every
    // (re)connect — otherwise live updates silently stop after a network blip.
    const joinAvailabilityRoom = () =>
      SOCKET_CLIENT.emit(SOCKET_CLIENT_EVENTS.JOIN_MENU_AVAILABILITY, { restaurant_id: restaurantId });

    // Listen BEFORE joining to avoid a join/emit race.
    SOCKET_CLIENT.on(SOCKET_CLIENT_EVENTS.GET_DATA, handleSocketData);
    SOCKET_CLIENT.on("connect", joinAvailabilityRoom);
    joinAvailabilityRoom();
    return () => {
      SOCKET_CLIENT.off(SOCKET_CLIENT_EVENTS.GET_DATA, handleSocketData);
      SOCKET_CLIENT.off("connect", joinAvailabilityRoom);
      SOCKET_CLIENT.emit(SOCKET_CLIENT_EVENTS.LEAVE_MENU_AVAILABILITY, { restaurant_id: restaurantId });
    };
  }, [restaurantId]);

  const addForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      _id: '',
      type: '',
      image: '',
      item_name: '',
      item_name_ja: '',
      addons: [],
      spice_level: '',
      spice_level_needed: null,
      price: '',
      tax_price: '',
      total_price: '',
      short_description: '',
      short_description_ja: '',
      consumption_tax_rate: '',
      cost_price: ''
    },
    validationSchema: Yup.object({
      addons: Yup.array().of(
        Yup.object({
          item_id: Yup.string().required(t('select_item_text'))
        })
      )
    }),
    onSubmit: (values, { setSubmitting, resetForm }) => {
      setDisableBtn(true)
      let data = [...selectedItems];
      let index = -1;
      function getAddonArray(item) {
        return item.addons.map(getAddonKey);
      }
      if (values.type === ITEM_TYPES.SET_OF_ITEMS) {
        const item = values;
        const comboAddons = item.addons.map(getAddonKey);
        index = data.findIndex(record => record._id === item._id && isSameArray(comboAddons, getAddonArray(record)));
      } else {
        index = data.findIndex(item => item._id === values._id && item.spice_level === values.spice_level);
      }
      if (index >= 0) {
        data[index].quantity += 1;
      } else {
        data.push({
          ...values,
          quantity: 1,
          temp_item_id: getUniqueID('selected-item-')
        });
      }
      setSelectedItems(data);
      saveVisitCart(restaurantId, tableId, data)
      console.log("data", data, values)
      addFormCloseButton?.current?.click();
      addForm.resetForm();
    },
  });

  /** If user comes to edit an item */
  useEffect(() => {
    const editableId = queryParams.get('openEdit');
    if (editableId && editableId.length) {
      const data = selectedItems.filter(item => item?._id === editableId)[0];
      if (data) {
        fetchMenuItemDetail(editableId).then(() => {
          addForm.setValues(data);
          addFormOpenButton?.current?.click();
        }).catch((err) => {
          console.error("Error while getting info", err);
        })
      }
    }
  }, [location]);

  useEffect(() => {
    setSelectedItems(getStoredCartItems());
  }, [location.search]);

  useEffect(() => {
    if (token && token.length) {
      setCatLoader(true);
      categoryList({}, { token: token })
        .then((response) => {
          setCategoriesList(response.data.list);
          if (!selectedCategory) {
            setSelectedCategory(response.data.list[0]?._id);
          }
          setTimeout(() => {
            setCatLoader(false);
          }, LOADER_TIMEOUT_TIME);
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            window.dispatchEvent(new CustomEvent("qr-bootstrap-unauthorized", {
              detail: {
                source: "Category list",
                error: err,
              },
            }));
          }
          console.error(err);
          setCatLoader(false);
        });
    }
  }, [token, langChanged]);

  useEffect(() => {
    if (token && token.length && selectedCategory) {
      console.log("Sub-category fetch triggered for category:", selectedCategory);
      setSubCatLoader(true);
      setSubCategoriesList([]);
      setSelectedSubCategory('');

      subCategoryList({ category_id: selectedCategory }, { token: token })
        .then((response) => {
          console.log("Sub-category list received:", response.data.list);
          const list = response.data.list || [];
          setSubCategoriesList(list);

          if (list.length > 0) {
            const subId = queryParams.get('sub_category_id');
            if (subId && list.find(s => s._id === subId)) {
              setSelectedSubCategory(subId);
            } else {
              setSelectedSubCategory('');
              const searchParams = new URLSearchParams(window.location.search);
              searchParams.delete("sub_category_id");
              navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
              }, { replace: true });
            }
          } else {
            console.log("No sub-categories found for this category");
            setSelectedSubCategory('');
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete("sub_category_id");
            navigate({
              pathname: location.pathname,
              search: searchParams.toString(),
            }, { replace: true });
          }
          setSubCatLoader(false);
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            window.dispatchEvent(new CustomEvent("qr-bootstrap-unauthorized", {
              detail: {
                source: "Sub-category list",
                error: err,
              },
            }));
          }
          console.error("Sub-category fetch error:", err);
          setSubCatLoader(false);
          setSelectedSubCategory('');
        });
    }
  }, [token, selectedCategory, langChanged]);

  const fetchMenuItems = (page = QR_MENU_ITEMS_INITIAL_PAGE, append = false) => {
    if (append && isLoadingMoreItems) {
      return;
    }

    if (!selectedCategory) {
      setItemList([]);
      setHasMoreItems(false);
      setItemLoader(false);
      setIsLoadingMoreItems(false);
      if (!append) {
        stopQrNativeLoader(base);
      }
      return;
    }

    let filter = {
      category_id: selectedCategory,
      page,
      per_page: QR_MENU_ITEMS_PER_PAGE,
    };
    if (selectedSubCategory) {
      filter.sub_category_id = selectedSubCategory;
    }

    setItemLoader(!append);
    setIsLoadingMoreItems(append);

    menuItemList(filter, { token: token })
      .then((response) => {
        const list = response.data.list || [];
        const validItems = list.filter(isOrderableMenuItem);

        setItemList((previousItems) => append ? [...previousItems, ...validItems] : validItems);
        setItemPage(response.data.page || page);
        setHasMoreItems(Boolean(response.data.has_next_page));

        setTimeout(() => {
          setItemLoader(false);
          setIsLoadingMoreItems(false);
          if (!append) {
            stopQrNativeLoader(base);
          }
        }, LOADER_TIMEOUT_TIME)
      })
      .catch((err) => {
        console.error("Item list fetch error:", err);
        Swal.fire({
          icon: "error",
          text: t("menu_items_load_error", { defaultValue: "Unable to load menu items. Please try again." }),
          ...SWAL_SETTINGS,
        });
        setItemLoader(false);
        setIsLoadingMoreItems(false);
        if (!append) {
          stopQrNativeLoader(base);
        }
      });
  };

  const handleMenuItemsScroll = (event) => {
    if (!hasMoreItems || isLoadingMoreItems || itemLoader) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight <= QR_MENU_SCROLL_LOAD_OFFSET;

    if (isNearBottom) {
      fetchMenuItems(itemPage + 1, true);
    }
  };

  useEffect(() => {
    if (token && token.length && !subCatLoader) {
      fetchMenuItems();
    }
  }, [token, subCatLoader, selectedCategory, selectedSubCategory, langChanged]);

  // One unselected row of a group. `addon_id` is the group half of the price key
  // the backend resolves against; everything else is filled in when an item is
  // picked. No row identity is carried here — see the comment on `addonRowsByGroup`.
  const blankAddonRow = (group) => ({
    item_id: "",
    item_name: "",
    item_name_ja: "",
    item_variation: null,
    price: 0,
    tax_price: 0,
    total_price: 0,
    spice_level: null,
    spice_level_needed: false,
    // Repeats are emitted from this count when the order is placed
    // (reviewOrder) — one entry per unit, which is how the backend
    // prices quantity.
    quantity: 1,
    addon_id: group?._id,
    addon_name: group?.addon_name,
  });

  const fetchMenuItemDetail = (_id) => {
    return new Promise((resolve, reject) => {
      menuItemDetail({ o_id: _id }, { token: token }).then((response) => {
        setCurrentItem(response?.data);
        // The configured groups as first fetched. Rows are added and deleted
        // against this baseline, so it must stay exactly what the menu says —
        // never the live row list.
        setItemGroups(response?.data?.addons || []);
        setDisableBtn(false);
        if (response?.data.type === ITEM_TYPES.ITEM) {
          addForm.setValues({
            ...response.data,
            // spice_level: response?.data?.spice_level_needed ? SPICE_LEVELS.LOW : null,
            // spice_level: response?.data?.spice_level_needed ? (optionsData[0]?._id || null) : null,
            spice_level: response?.data?.spice_level_needed ? (optionsData[0] || null) : null,
            spice_level_needed: response?.data?.spice_level_needed || false,
          });
        } else {
          // One row per configured group entry — a group the menu lists twice
          // starts as two rows, and that count is also the floor the diner
          // cannot delete below.
          let data = {
            ...response?.data,
            addons: response?.data?.addons?.map(blankAddonRow),
          };
          addForm.setValues(data);
        }
        setTimeout(() => {
          resolve(response);
        }, 100);
      }).catch((err) => {
        if (err?.response?.status === 401) {
          window.dispatchEvent(new CustomEvent("qr-bootstrap-unauthorized", {
            detail: {
              source: "Menu item detail",
              error: err,
            },
          }));
        }
        console.error(err);
        reject(err);
      })
    })
  }

  // The rows ARE `addForm.values.addons` — one array, no parallel copy to keep in
  // step. Every mutation goes through here so the line price is never left
  // stale.
  // The picker hands back an updater rather than a list, so the step is applied
  // to the rows the form holds NOW — two taps in one batch both land.
  //
  // Applied to a ref rather than to `addForm.values.addons`: reading the rendered
  // values is exactly the read the updater exists to avoid, since two steps
  // dispatched inside one React batch would both see the pre-batch array and the
  // second would drop the first. The ref is re-seeded from the form on every
  // render, so it follows a reset or a re-open, and is advanced here so a second
  // call in the same batch composes on top of the first.
  const addonRowsRef = useRef([]);
  addonRowsRef.current = addForm.values.addons || [];

  const applyAddonRows = (update) => {
    const updatedAddons = update(addonRowsRef.current);
    addonRowsRef.current = updatedAddons;
    addForm.setFieldValue('addons', updatedAddons);
    calculateComboPrice(updatedAddons);
  };

  // Quantity is a count on the entry here, not repeat entries: the cart line has
  // to stay editable, so the expansion into one entry per unit happens once at
  // the boundary where the order is placed (reviewOrder's expandAddonQuantities).
  // Which rows exist, what each carries and how many units it holds is the
  // picker's business now — it hands back the whole list and this re-prices it.

  const calculateComboPrice = (updatedAddons) => {
    if (addForm.values.type !== ITEM_TYPES.SET_OF_ITEMS) return;
    // A quantity of N contributes its gross N times, mirroring the N entries the
    // order payload carries and the N premiums the backend prices.
    const addons = expandByQuantity(
      (updatedAddons || addForm.values.addons).filter((a) => a.item_id)
    );
    // Base item gross + chosen component grosses, with the line's tax extracted
    // from that total. Shares calculateSetMenuPrice with the cashier picker,
    // which mirrors the backend's authoritative sumSetLine.
    const { price, tax_price, total_price } = calculateSetMenuPrice(
      currentItem,
      addons,
      ChargeSettings?.tax_when_register_product
    );
    addForm.setFieldValue('price', price);
    addForm.setFieldValue('tax_price', tax_price);
    addForm.setFieldValue('total_price', total_price);
  };

  useEffect(() => {
    let result = 0;
    selectedItems.forEach((record) => {
      result += record?.quantity;
    });
    dispatch(SetCartLength(result));
  }, [selectedItems]);

  useEffect(() => {
    const handleLanguageChange = () => {
      setLangChanged((currentValue) => !currentValue);
    };
    document.addEventListener('language_change', handleLanguageChange);
    return () => {
      document.removeEventListener('language_change', handleLanguageChange);
    };
  }, []);
  const isItemSelected = (item) => {
    return selectedItems?.findIndex(record => record?._id === item?._id) !== -1;
  };
  const getCurrentQuantity = (item) => {
    let result = 0;
    const records = selectedItems?.filter(record => record?._id === item?._id);
    records.forEach((record) => {
      result += record?.quantity;
    });
    return result;
  };
  const handleChangeQuantity = async (item, type) => {

    let allItems = [...selectedItems];
    if (type === "increase") {
      // Plain items (no spice level, no addon group) need no configuration, so
      // increment the existing cart line directly instead of reopening the
      // item drawer. Configurable items still open the drawer. (WRDS-523)
      if (item?.type === ITEM_TYPES.ITEM && !item?.spice_level_needed) {
        const index = allItems.findIndex((record) => record?._id === item?._id);
        if (index !== -1) {
          allItems[index].quantity += 1;
        }
      } else {
        addFormOpenButton?.current?.click();
        await fetchMenuItemDetail(item?._id);
      }
    } else {
      const commonItems = allItems.filter((record) => record?._id === item?._id);
      if (commonItems.length > 1) {
        setRemovableItems(commonItems);
        removableItemsOpenButton?.current?.click();
      } else {
        const index = allItems.findIndex((record) => record?._id === item?._id);
        if (allItems[index].quantity > 1) {
          allItems[index].quantity -= 1;
        } else {
          allItems.splice(index, 1);
        }
      }
    }
    setSelectedItems(allItems);
    saveVisitCart(restaurantId, tableId, allItems)
  };

  const handleAddItemClick = (item) => {
    addFormOpenButton?.current?.click();
    fetchMenuItemDetail(item?._id);
  };

  const deleteSubQuantity = (item) => {
    let primaryItems = [...selectedItems];
    let tempItems = [...removableItems];
    const primaryIndex = primaryItems.findIndex((record) => record?.temp_item_id === item?.temp_item_id);
    const secondaryIndex = tempItems.findIndex((record) => record?.temp_item_id === item?.temp_item_id);
    if (item.quantity > 1) {
      primaryItems[primaryIndex].quantity -= 1;
    } else {
      if (primaryIndex !== -1) {
        primaryItems.splice(primaryIndex, 1);
      }
      if (secondaryIndex !== -1) {
        tempItems.splice(secondaryIndex, 1);
      }
    }
    setRemovableItems(tempItems);
    setSelectedItems(primaryItems);
    saveVisitCart(restaurantId, tableId, primaryItems)
    if (tempItems.length === 0) {
      removableItemsCloseButton?.current?.click();
    }
  };

  const handleAsterisk = (item) => {
    let result = null;
    if (
      item?.consumption_tax_rate === CONSUMPTION_TAX_RATES.TAKE_OUT &&
      ChargeSettings?.icon_display_eligible_for_reduced_tax_rate
    ) {
      result = <span className="fw-bold">*</span>;
    }
    return result;
  };

  // const getSpiceName = (spiceId) => {
  //   const found = optionsData?.find(s => s._id === spiceId);
  //   return found?.name || spiceId;
  // };

  const getSpiceName = (spiceLevel) => {
  if (!spiceLevel) return '';
  if (typeof spiceLevel === 'object') {
    return getTranslatedKey('name', spiceLevel) || spiceLevel.name;
  }
  const found = optionsData?.find(s => s._id === spiceLevel);
  return found ? (getTranslatedKey('name', found) || spiceLevel) : spiceLevel;
};

  // The sheet head shows one number: what the line costs as it stands. On a set
  // that is the base plus every component taken so far, re-priced on each step.
  const sheetTotal = Number(addForm?.values?.total_price) || 0;

  const sheetDescription = getDisplayableDescription(getTranslatedKey('short_description', addForm?.values));

  return (
    <>
      <div className="qr-body-wrap">
        <div className="menu-qr-main-wrap">
          <div className="qr-menu-left">
            {!catLoader ? (
              <div className="qr-menu-list">
                {categoriesList.length > 0
                  ? categoriesList.map((item, index) => {
                    return (
                      <button
                        className={"menu-btn" + (selectedCategory === item._id ? ` active` : "")}
                        key={index}
                        onClick={() => {
                          setSelectedCategory(item?._id);
                          setSelectedSubCategory('');
                          setSubCategoriesList([]);
                          setItemLoader(true);
                          setItemList([]);
                          const searchParams = new URLSearchParams(window.location.search);
                          searchParams.set("category_id", item?._id);
                          searchParams.delete("sub_category_id");
                          navigate({
                            pathname: location.pathname,
                            search: searchParams.toString(),
                          });
                        }}
                      >
                        {getTranslatedKey('name', item)}{" "}
                      </button>
                    );
                  })
                  : ""}
                {/* <button className='menu-btn active'>Fries </button> */}
              </div>
            ) : (
              <div className="qr-menu-list">
                <Skeleton style={{ height: "37px" }} />
                <Skeleton style={{ height: "37px" }} />
                <Skeleton style={{ height: "37px" }} />
                <Skeleton style={{ height: "37px" }} />
                <Skeleton style={{ height: "37px" }} />
              </div>
            )}
          </div>
          <div className="qr-menu-right" onScroll={handleMenuItemsScroll}>
            {!subCatLoader ? (
              subCategoriesList.length > 0 && (
                <div className="qr-subcategory-list">
                  {subCategoriesList.map((item, index) => (
                    <button
                      key={index}
                      className={"subcategory-btn" + (selectedSubCategory === item._id ? " active" : "")}
                      onClick={() => {
                        const nextSubCategory = selectedSubCategory === item._id ? '' : item._id;
                        setSelectedSubCategory(nextSubCategory);
                        setItemLoader(true);
                        setItemList([]);
                        const searchParams = new URLSearchParams(window.location.search);
                        if (nextSubCategory) {
                          searchParams.set("sub_category_id", nextSubCategory);
                        } else {
                          searchParams.delete("sub_category_id");
                        }
                        navigate({
                          pathname: location.pathname,
                          search: searchParams.toString(),
                        }, { replace: true });
                      }}
                    >
                      {getTranslatedKey('name', item)}
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div className="qr-subcategory-list">
                <Skeleton width={80} height={30} count={3} inline={true} style={{ marginRight: '10px', borderRadius: '6px' }} />
              </div>
            )}
            {!itemLoader ? (
              <>
                {itemList.length ? (
                  <>
                    <div className="qr-menu-item-list">
                      {itemList.map((item) => {
                        return (
                          <div className={"qr-menu-item-col" + (isItemSelected(item) ? " item-selected" : "") + (item?.is_available === false ? " sold-out" : "")} key={item._id}>
                            <figure>
                              <img src={item?.image} alt={item?.item_name} />
                              {item?.is_available === false && <span className="sold-out-badge">{t("label_sold_out")}</span>}
                            </figure>
                            <figcaption>
                              <div>
                                {/* name and asterisk are separate spans so the
                                    three line clamp on the name cannot swallow
                                    the reduced tax marker */}
                                <span className="qr-menu-item-name">
                                  <span className="qr-menu-item-name-text">{getTranslatedKey('item_name', item)}</span>
                                  {handleAsterisk(item)}
                                </span>
                                {/* <span>¥ {showPrice(item?.price)}</span> */}
                                <span>¥ {showPrice(item?.total_price)}</span>
                              </div>
                              {/* <button
                              className="btn btn-sm btn-primary"
                              data-bs-toggle="offcanvas"
                              data-bs-target="#itemModifyCanvas"
                              aria-controls="itemModifyCanvas"
                              onClick={() => {
                                fetchMenuItemDetail(item?._id);
                              }}
                            >
                              {isItemSelected(item) ? t("btn_update") : t("link_add")}
                            </button> */}

                              {isItemSelected(item) ?

                                <div className="border border-1 rounded-1 mb-2 quantity-box d-flex justify-content-between">
                                  <button
                                    className="btn btn-sm py-1 px-2 h-auto"
                                    type="button"
                                    onClick={() => {
                                      handleChangeQuantity(
                                        item,
                                        "decrease",
                                        // getSelectedItem(item)?.quantity
                                        getCurrentQuantity(item)
                                      );
                                    }}
                                  >
                                    <i className="ti ti-minus"></i>
                                  </button>
                                  <span className="d-flex align-items-center">
                                    {getCurrentQuantity(item)}
                                  </span>
                                  <button
                                    className="btn btn-sm py-1 px-2 h-auto"
                                    type="button"
                                    disabled={item?.is_available === false}
                                    onClick={() => {
                                      handleChangeQuantity(
                                        item,
                                        "increase",
                                        getCurrentQuantity(item)
                                      );
                                    }}
                                  >
                                    <i className="ti ti-plus"></i>
                                  </button>
                                </div>

                                :

                                item?.is_available === false ?

                                <button className="btn btn-sm btn-secondary" type="button" disabled>
                                  {t("label_sold_out")}
                                </button>

                                :

                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => {
                                    handleAddItemClick(item);
                                  }}
                                >
                                  {t("link_add")}
                                </button>
                              }
                            </figcaption>
                          </div>
                        );
                      })}
                    </div>
                    <ReducedTaxFootnote items={selectedItems} chargeSettings={ChargeSettings} t={t} />
                    {isLoadingMoreItems ? <InlineLoader /> : null}
                  </>
                ) : (
                  <div className="">
                    <RecordNotFound heading="no_assigned_item_text" />
                  </div>
                )}
              </>
            ) : (
              <div className="qr-menu-item-list">
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
                <div className="qr-menu-item-col">
                  <Skeleton style={{ height: "70px" }} className="rounded-3 mb-3" />
                  <figcaption>
                    <Skeleton containerClassName="d-flex" />
                    <Skeleton containerClassName="d-flex" inline={true} style={{ height: "10px" }} />
                    <Skeleton containerClassName="d-flex" style={{ height: "33px" }} />
                  </figcaption>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {CartLengthData ? (
        <div className="cart-bottom p-3">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span>
              {CartLengthData} {pluralText(CartLengthData, t("label_item"), t("label_items"))}
            </span>
            {
              ChargeSettings?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED ?
                <span>¥ {showPrice(getSubTotalQRApp(selectedItems)?.subtotal)}</span>
                :
                <span>¥ {showPrice(getSubTotalQRApp(selectedItems)?.subtotal + getSubTotalQRApp(selectedItems)?.tax_price)}</span>
            }
          </div>
          <button
            className="btn btn-primary w-100"
            onClick={() => {
              const searchParams = window.location.search;
              navigate(`/qr-order/review${searchParams}`, {
                state: {
                  selectedItems: selectedItems,
                  customerDetails: customerDetails,
                },
              });
            }}
          >
            {base === ORDER_PLATFORM.WEB ? t("review_place_order_text") : t("button_text_next")}
          </button>
        </div>
      ) : null}
      <button className="btn btn-sm btn-primary d-none" ref={addFormOpenButton} data-bs-toggle="offcanvas" data-bs-target="#itemModifyCanvas" aria-controls="itemModifyCanvas"></button>
      <div className="offcanvas offcanvas-bottom qr-offcanvas" data-bs-backdrop="static" tabIndex="-1" id="itemModifyCanvas" aria-labelledby="itemModifyCanvasLabel">
        <div className="offcanvas-header p-0">
          {/* Grows into the space the close button leaves, so the price can sit
              at the far end of the row rather than beside the name. */}
          <h5 className="offcanvas-title p-3 flex-grow-1" id="itemModifyCanvasLabel">
            {addForm?.values?.item_name ? (
              // The name holds the left edge and the price the right: centring the
              // pair moved the NAME every time the price grew a digit.
              <div className="d-flex align-items-center justify-content-between gap-2">
                <span className="qr-sheet-item-name">{getTranslatedKey('item_name', addForm?.values)}</span>
                {hasDisplayablePrice(addForm?.values?.total_price) ? (
                  <small className="qr-sheet-item-price text-nowrap">
                    ¥ {showPrice(sheetTotal)}
                  </small>
                ) : null}
              </div>
            ) : (
              <div className="qr-menu-list">
                <Skeleton style={{ height: "20px", width: "250px" }} active={true} />
              </div>
            )}
          </h5>
          <button
            type="button"
            className="btn-close me-0 p-3 d-block"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            ref={addFormCloseButton}
            onClick={() => {
              const searchParams = new URLSearchParams(window.location.search);
              searchParams.delete("openEdit");
              navigate({
                pathname: location.pathname,
                search: searchParams.toString(),
              });
              addForm.resetForm();
            }}
          ></button>
        </div>

        <div className="offcanvas-body small">
          {addForm?.values?.image ? (
            <figure className="qr-sheet-item-figure mb-3">
              <img
                src={addForm?.values?.image}
                alt={getTranslatedKey('item_name', addForm?.values)}
              />
            </figure>
          ) : null}
          {sheetDescription ? (
            <p className="mb-3 d-block">{sheetDescription}</p>
          ) : null}
          <form onSubmit={addForm.handleSubmit}>

            {
              addForm?.values?.item_name ? (
                <>
                  {addForm?.values?.type === ITEM_TYPES.ITEM ?
                    (
                      <>
                        {addForm?.values?.spice_level_needed ? (
                          <>
                            <div className="card card-2">
                              <div className="p-3">
                                <div className="row-veg-card">
                                  <div className="head-veg-card d-flex align-items-center justify-content-between mb-2">
                                    <h4>{t("spice_level_text")}</h4>
                                  </div>
                                  <div className="head-veg-card d-flex align-items-center justify-content-between mb-3">
                                    <h4>{getTranslatedKey('item_name', addForm?.values) || getTranslatedKey('addon_name', addForm?.values)}</h4>
                                  </div>
                                  <div className="veg-tags">
                                    <ul>
                                      {optionsData?.map((spice) => (
                                        <li key={spice._id}>
                                          <div className="check-input-tick">
                                            <input
                                              type="radio"
                                              className="cp"
                                              name="spice_level"
                                              id={"spice_level_" + spice._id + "_" + addForm?.values?._id}
                                              value={spice._id}
                                              // checked={addForm?.values?.spice_level === spice._id}
                                              // onChange={addForm.handleChange}
                                              checked={addForm?.values?.spice_level?._id === spice._id}
                                              onChange={() => addForm.setFieldValue('spice_level', spice)}
                                            />
                                            <label htmlFor={"spice_level_" + spice._id + "_" + addForm?.values?._id} className="cp">
                                              <i className="ti ti-circle-check"></i> {getTranslatedKey('name', spice) || spice?.name}
                                            </label>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {/* One collapsible section per configured group, listing
                            that group's options. Shared with the order summary's
                            edit modal so a set line is chosen the same way on
                            both screens. */}
                        <SetMenuAddonPicker
                          groups={itemGroups}
                          rows={addForm.values.addons}
                          spiceOptions={optionsData}
                          onChangeRows={applyAddonRows}
                        />
                      </>
                    )}
                </>
              ) :
                <div className="qr-menu-list w-100">
                  <Skeleton style={{ height: "20px", width: "60%" }} count={1} active={true} />
                  <Skeleton style={{ height: "20px", width: "100%" }} count={1} active={true} />
                  <Skeleton style={{ height: "60px", width: "100%" }} count={1} active={true} />
                </div>
            }

            {/* Would you like to have more - Upsell Items */}
            {currentItem?.upsell_items_data?.length > 0 && (
              <div className="mt-4 upsell-section">
                <h5 className="mb-2 upsell-title">{t("would_you_like_more")}</h5>
                <div className="upsell-list">
                  {currentItem?.upsell_items_data.map((upsellItem, index) => (
                    <div key={index} className="upsell-card text-center">
                      <img
                        src={upsellItem?.image}
                        alt={getTranslatedKey('item_name', upsellItem)}
                        className="upsell-card-image"
                      />
                      <div className="fw-semibold mt-1 upsell-card-name">
                        {TrimText(getTranslatedKey('item_name', upsellItem), 12)}
                      </div>
                      <small className="text-muted d-block upsell-card-price">¥ {showPrice(upsellItem?.total_price)}</small>
                      <div className="mt-1 upsell-card-action">
                        {isItemSelected(upsellItem) ? (
                          <div className="border border-1 rounded-1 mb-2 quantity-box d-flex justify-content-between upsell-quantity-box">
                            <button className="btn btn-sm py-1 px-2 h-auto" type="button"
                              onClick={() => {
                                let allItems = [...selectedItems];
                                const commonItems = allItems.filter((record) => record?._id === upsellItem?._id);
                                if (commonItems.length > 1) {
                                  setRemovableItems(commonItems);
                                  removableItemsOpenButton?.current?.click();
                                } else {
                                  const idx = allItems.findIndex((record) => record?._id === upsellItem?._id);
                                  if (idx !== -1) {
                                    if (allItems[idx].quantity > 1) {
                                      allItems[idx].quantity -= 1;
                                    } else {
                                      allItems.splice(idx, 1);
                                    }
                                  }
                                }
                                setSelectedItems([...allItems]);
                                saveVisitCart(restaurantId, tableId, allItems);
                              }}>
                              <i className="ti ti-minus"></i>
                            </button>
                            <span className="d-flex align-items-center upsell-quantity-count">
                              {getCurrentQuantity(upsellItem)}
                            </span>
                            <button className="btn btn-sm py-1 px-2 h-auto" type="button"
                              onClick={() => {
                                let allItems = [...selectedItems];
                                const idx = allItems.findIndex(i => i._id === upsellItem._id);
                                if (idx >= 0) {
                                  allItems[idx].quantity += 1;
                                }
                                setSelectedItems([...allItems]);
                                saveVisitCart(restaurantId, tableId, allItems);
                              }}>
                              <i className="ti ti-plus"></i>
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-sm btn-primary w-100 upsell-add-btn"
                            type="button"
                            onClick={() => {
                              let allItems = [...selectedItems];
                              menuItemDetail({ o_id: upsellItem?._id }, { token: token }).then((response) => {
                                const itemData = response?.data;
                                const index = allItems.findIndex(i => i._id === itemData._id && i.spice_level === (itemData?.spice_level_needed ? SPICE_LEVELS.LOW : null));
                                if (index >= 0) {
                                  allItems[index].quantity += 1;
                                } else {
                                  allItems.push({
                                    ...itemData,
                                    spice_level: itemData?.spice_level_needed ? SPICE_LEVELS.LOW : null,
                                    quantity: 1,
                                    temp_item_id: getUniqueID('selected-item-')
                                  });
                                }
                                setSelectedItems(allItems);
                                saveVisitCart(restaurantId, tableId, allItems);
                              }).catch((err) => {
                                if (err?.response?.status === 401) {
                                  window.dispatchEvent(new CustomEvent("qr-bootstrap-unauthorized", {
                                    detail: {
                                      source: "Upsell item detail",
                                      error: err,
                                    },
                                  }));
                                }
                                console.error(err);
                              });
                            }}
                          >
                            {t("link_add")}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Dead while any row of a set is still unchosen — the same rule the
                summary's edit modal applies, and the same one the cashier's
                picker puts on its confirm. A plain item has no rows to check. */}
            {/* disableBtn goes true on submit, so a second tap before the sheet
                closes cannot add the item twice. */}
            <button
              className="btn btn-primary w-100 mt-4 addon-submit-btn"
              type="submit"
              disabled={
                disableBtn ||
                !addForm?.values?.item_name ||
                (addForm?.values?.type !== ITEM_TYPES.ITEM &&
                  (addForm?.values?.addons || []).some((addon) => !addon?.item_id))
              }
            >
              {t("link_add")} {hasDisplayablePrice(addForm?.values?.total_price) ? `: ¥ ${showPrice(addForm?.values?.total_price)}` : ''}
            </button>
          </form>
        </div >
      </div >

      <button
        className="btn btn-dark w-100 mt-2 d-none"
        data-bs-toggle="modal"
        data-bs-target="#removeItemsOptions"
        ref={removableItemsOpenButton}
      ></button>
      <div
        className="modal fade qr-modal"
        id="removeItemsOptions"
        tabIndex="-1"
        aria-labelledby="removeItemsOptionsLabel"
        aria-hidden="true"
        data-bs-backdrop="static"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">{t("link_remove")} {t("label_items")}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={removableItemsCloseButton}></button>
            </div>
            <div className="modal-body">
              <h4>{t("label_items")} </h4>
              <div className="d-flex gap-2 flex-column">
                {removableItems?.map(
                  (item, index) => {
                    return (
                      <div className="card card-2" key={index}>
                        <div className="p-2">
                          <div className="d-flex align-items-center justify-content-between">
                            <div className="">
                              <span className="d-block">
                                {getTranslatedKey("item_name", item)}{" "}
                                {handleAsterisk(item)}{" "}
                                {item?.quantity > 1 ? (
                                  <strong>{`(x${item?.quantity})`}</strong>
                                ) : null}
                                <br />
                                {item?.spice_level ? (
                                  <span className="fs-12">
                                    {t("spice_level_text")} -{" "}
                                    <strong>
                                      {/* {t("label_" + item?.spice_level)} */}
                                      {getSpiceName(item?.spice_level)}
                                    </strong>
                                  </span>
                                ) : null}
                              </span>
                              {item?.type === ITEM_TYPES.SET_OF_ITEMS ? (
                                <div className="w-100 pt-2">
                                  {item?.addons?.map(
                                    (addon, addonIndex) => {
                                      return (
                                        <div
                                          className={
                                            "border p-2 rounded-2 mb-2" +
                                            (addonIndex + 1 <
                                              item?.addons?.length
                                              ? " me-1"
                                              : "")
                                          }
                                          key={addonIndex}
                                        >
                                          <span className="d-block fs-12">
                                            {getTranslatedKey(
                                              "item_name",
                                              addon
                                            )}
                                            {addon?.count > 1 ? (
                                              <strong>{`(x${addon?.count})`}</strong>
                                            ) : null}
                                          </span>
                                          {addon?.spice_level ? (
                                            <span className="d-block fs-12">
                                              {t("spice_level_text")} -{" "}
                                              <strong>
                                                {/* {t(
                                                  "label_" +
                                                  addon?.spice_level
                                                )} */}
                                                {getSpiceName(addon?.spice_level)}
                                              </strong>
                                            </span>
                                          ) : null}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              ) : null}
                              <small>

                                ¥ {ChargeSettings?.display_method_product_price === DISPLAY_METHOD_TAX_OPTIONS.TAX_EXCLUDED ? showPrice(item?.price * item?.quantity) : showPrice(item?.total_price * item?.quantity)}
                              </small>
                            </div>
                            <div>
                              <div className="d-flex gap-2">
                                <button
                                  className="btn btn-icon btn-danger"
                                  type="button"
                                  onClick={() => {
                                    deleteSubQuantity(item);
                                  }}
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
              <ReducedTaxFootnote items={removableItems} chargeSettings={ChargeSettings} t={t} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
